// ─────────────────────────────────────────────────────────────────────────────
// authToken — single source of truth for the JWT access token.
//
// Codex WARNING fix (prep work): the codebase had `localStorage.getItem`/
// `setItem`/`removeItem` for "accessToken" scattered across 9 files. Moving
// to httpOnly cookies (Codex's recommendation) requires touching every one
// of those call sites. This module centralises the contract so the eventual
// migration is a one-file change.
//
// **Today's storage backend:** localStorage. This is XSS-exposable. The
// shared ApiCall axios instance still picks up the value via getAccessToken().
//
// **Tomorrow's storage backend:** httpOnly cookies. To migrate:
//   1. Backend Set-Cookie on /auth/login response with HttpOnly + Secure +
//      SameSite=Strict; refresh-token cookie on /auth/refresh.
//   2. Backend reads cookie on every protected route (server-side; no
//      client involvement).
//   3. In this file: change getAccessToken() to return null (no client
//      access), drop setAccessToken(), keep clearAccessToken() but make it
//      POST to /auth/logout to ask the server to clear the cookie.
//   4. In ApiCall: drop the Authorization header — cookies are attached
//      automatically. Add `withCredentials: true` to the axios instance.
//   5. Validate on staging — make sure CORS and CSRF protections still work.
//
// **Idle timeout:** also exported here so the whole app shares one timer.
// Default 30 min of no recorded activity → auto-purge token.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = "accessToken";
const PERSIST_ROOT_KEY = "persist:root";
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// In-memory mirror so other tabs invalidating localStorage don't immediately
// break the current tab's API calls if mid-flight. Falls back to localStorage
// on cold reload (which then re-validates exp via sessionExpiry.js).
let _inMemoryToken = null;
let _idleTimer = null;
let _onIdleHandler = null;

/**
 * @returns {string | null}
 */
export function getAccessToken() {
  if (_inMemoryToken) return _inMemoryToken;
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t) _inMemoryToken = t;
    return t;
  } catch (_) {
    // localStorage may be inaccessible (private mode, etc.).
    return null;
  }
}

/**
 * @param {string} token
 */
export function setAccessToken(token) {
  _inMemoryToken = token || null;
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (_) {
    // best-effort
  }
  resetIdleTimer();
}

/**
 * Wipe the access token and the persisted Redux root entry so user-side
 * caches don't survive logout / session expiry.
 */
export function clearAccessToken() {
  _inMemoryToken = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Also wipe redux-persist's snapshot of userSlice + ContactSlice — those
    // are the two whitelisted slices in store.js. Otherwise the next page
    // load rehydrates stale auth.
    localStorage.removeItem(PERSIST_ROOT_KEY);
  } catch (_) {
    // best-effort
  }
  if (_idleTimer) {
    clearTimeout(_idleTimer);
    _idleTimer = null;
  }
}

/**
 * Register a callback that fires when the idle timer expires. Typical
 * implementation: dispatch the userSlice `logout` action and redirect to
 * /login. Call this from a top-level component on mount.
 *
 * Returns an unsubscribe function for use in useEffect cleanup.
 *
 * @param {() => void} fn
 * @returns {() => void}
 */
export function onIdleLogout(fn) {
  _onIdleHandler = fn;
  resetIdleTimer();
  // Track activity on common interaction events. We listen at the document
  // level so any in-app interaction resets the timer. Throttle reset to
  // once per 5 s so we don't churn timers on every mousemove.
  let lastReset = 0;
  const handler = () => {
    const now = Date.now();
    if (now - lastReset < 5000) return;
    lastReset = now;
    resetIdleTimer();
  };
  const events = ["mousedown", "keydown", "touchstart", "wheel"];
  events.forEach((ev) => document.addEventListener(ev, handler, { passive: true }));
  return () => {
    events.forEach((ev) => document.removeEventListener(ev, handler));
    if (_idleTimer) clearTimeout(_idleTimer);
    _idleTimer = null;
    _onIdleHandler = null;
  };
}

function resetIdleTimer() {
  if (_idleTimer) clearTimeout(_idleTimer);
  if (!_onIdleHandler) return;
  if (!getAccessToken()) return; // nothing to log out of
  _idleTimer = setTimeout(() => {
    const fn = _onIdleHandler;
    if (fn) fn();
  }, IDLE_TIMEOUT_MS);
}

export const __test__ = {
  STORAGE_KEY,
  PERSIST_ROOT_KEY,
  IDLE_TIMEOUT_MS,
};
